import { popModal, pushModal } from "components/modal/store";
import { useLiabilityForm } from "../../hooks/useLiabilityForm"
import Button from "components/button";
import { ELiabilityType, type FullInvestment, type ILiability, type ISavedDocument } from "findef";
import { FindexClient } from "@/integrations/findex";
import { pushToast } from "components/toast/hooks";
import { Form } from "@/form/useForm";
import { step1Layout } from "../../hooks/useLiabilityForm/layouts/steps/step1";
import { step2Layout } from "../../hooks/useLiabilityForm/layouts/steps/step2";
import type { LiabilityFormLayout } from "../../hooks/useLiabilityForm/types";
import { computed, watch, type Ref } from "vue";

export type QuickAddLiabilityModalProps = {
  liabilityName?: string;
  liabilityType?: ELiabilityType;
  currentInvestment?: Ref<FullInvestment>;
}

export const pushQuickAddLiabilityModal = (props: QuickAddLiabilityModalProps = {}) => {
  const formHook = useLiabilityForm({
    currentInvestment: props.currentInvestment
  });

  if (props.currentInvestment) {
    watch(props.currentInvestment, () => {
      formHook.form.setFieldValue("hasInvestments", true);
    }, { immediate: true });
  }

  if (props.liabilityName) {
    formHook.form.setFieldValue('name', props.liabilityName);
  }

  formHook.form.setFieldValue('type', props.liabilityType || ELiabilityType.DEBT);


  return new Promise<ISavedDocument<ILiability, string> | null>((resolve) => {
    const handleCancel = () => {
      popModal();
      resolve(null);
    }

    const handleAdd = async () => {
      if (formHook.form.errors.value.length > 0) return;
      const state = formHook.form.cast();
      try {
        const [liab] = await FindexClient.createLiability(state);
        if (liab) {
          resolve(liab);
          pushToast({ variant: 'success', title: 'Liability Created', subtitle: 'The liability was created and added to your portfolio.' })
          popModal();
        } else {
          pushToast({ variant: 'error', title: 'Error', subtitle: 'There was an unexpected error, please try again later.' });
        }
      } catch (e) {
        pushToast({ variant: 'error', title: 'Error', subtitle: 'There was an error, please try again later.' });
        console.error(e);
      }
    }

    const layout: LiabilityFormLayout = {
      attributes: {
        class: 'grid grid-auto-rows gap-12 border border border-gray-300 p-4 md:p-8'
      },
      children: [
        step1Layout({ withOwnership: true }),
        // step2Layout({ withRelatedInvestments: true }),
        {
          title: 'Loan Information',
          description: 'Information you can choose to add now or later.',
          collapsible: true,
          key: 'quick-add-step2Layout-wrapper',
          children: [
            step2Layout({ withRelatedInvestments: true }),
          ]
        }
      ]
    }

    const saveIsDisabled = computed(() => {
      return formHook.form.errors.value.length > 0;
    });

    setTimeout(() => {
      formHook.form.updateErrors();
      formHook.form.updateAllFieldErrors();
      formHook.form.updateErrorList();
    }, 300)

    pushModal({
      title: 'Quick Add Liability',
      body: () => {
        return <div>
          <Form form={formHook.form} layout={layout} />
        </div>;
      },
      actions: computed(() => {
        return <div class="flex flex-row items-center justify-end gap-4">
          <Button variant="transparent" outlined onClick={handleCancel}>
            Cancel
          </Button>
          <Button disabled={saveIsDisabled.value} onClick={handleAdd}>
            Create
          </Button>
        </div>
      }),
      onClose: () => {
        resolve(null);
      },
      //bodyClass: ' ',
      style: {
        maxWidth: '820px',
        maxHeight: 'clamp(300px, 70svh, 90svh)',
        minWidth: 'unset',
        width: 'unset'
      }
    });
  });
}
